import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { EmptyState } from "../components/EmptyState";
import type { Store } from "../types";

export default function StoresPage() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["stores"],
    queryFn: () => api.get<{ stores: Store[] }>("/api/stores"),
  });

  const [name, setName] = useState("");
  const create = useMutation({
    mutationFn: (n: string) => api.post("/api/stores", { name: n }),
    onSuccess: () => {
      setName("");
      qc.invalidateQueries({ queryKey: ["stores"] });
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/api/stores/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stores"] }),
  });

  const list = data?.stores ?? [];

  return (
    <div>
      <PageHeader
        icon="🏪"
        title="Obchody"
        description="Definujte své obchody a jejich sekce v pořadí, ve kterém jimi procházíte."
      />

      <div className="card card-body mb-6">
        <h2 className="font-semibold mb-3 flex items-center gap-2">
          <span aria-hidden>➕</span>
          <span>Přidat obchod</span>
        </h2>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            className="input"
            placeholder="např. Albert Vinohrady"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button
            className="btn-primary flex items-center gap-1"
            disabled={!name.trim() || create.isPending}
            onClick={() => create.mutate(name.trim())}
          >
            <span aria-hidden>➕</span>
            <span>Přidat</span>
          </button>
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState
          icon="🏪"
          title="Zatím nemáte žádné obchody"
          description="Po přidání obchodu mu definujte sekce ve správném pořadí."
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((s) => (
            <div key={s.id} className="card card-body">
              <div className="flex items-start justify-between gap-2">
                <Link to={`/obchody/${s.id}`} className="font-semibold text-brand-700 hover:underline flex items-center gap-1">
                  <span aria-hidden>🏪</span>
                  <span>{s.name}</span>
                </Link>
                <button
                  className="btn-ghost text-xs text-red-600 flex items-center gap-1"
                  title="Smazat obchod"
                  onClick={() => {
                    if (confirm(`Smazat obchod "${s.name}" včetně jeho sekcí?`)) del.mutate(s.id);
                  }}
                >
                  <span aria-hidden>🗑️</span>
                  <span>Smazat</span>
                </button>
              </div>
              <Link className="text-xs text-slate-500 mt-2 inline-block" to={`/obchody/${s.id}`}>
                Spravovat sekce →
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
